<script setup lang="ts">
import { computed } from 'vue'
import { Minus, Plus } from '@lucide/vue'

/**
 * PNumberInput — champ numérique du design system PreLLM.
 *
 * Masque les spinners natifs (moches, incohérents entre navigateurs) et les
 * remplace par des steppers −/+ minimalistes. Remplace les
 * `<input type="number" class="p-input">` bruts.
 *
 * v-model number (coercion : vide → 0, cohérent avec la sentinelle « 0 =
 * désactivé » des quotas/poids). min/max clampent les steppers ET la saisie.
 * Label optionnel (comme PInput) ; sans label = contrôle nu (builders).
 * `stepper=false` masque les boutons pour les champs très étroits.
 */
interface Props {
    modelValue?: number
    label?: string
    placeholder?: string
    size?: 'sm' | 'md' | 'lg'
    disabled?: boolean
    min?: number
    max?: number
    step?: number
    /** Texte court à droite du label (ex: « € », « max 32 »). */
    suffix?: string
    hint?: string
    error?: string
    required?: boolean
    stepper?: boolean
    id?: string
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: 0,
    size: 'md',
    disabled: false,
    step: 1,
    stepper: true,
    required: false,
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: number): void
}>()

function clamp(value: number): number {
    let out = value
    if (props.min !== undefined && out < props.min) out = props.min
    if (props.max !== undefined && out > props.max) out = props.max
    return out
}

function onInput(event: Event) {
    const raw = (event.target as HTMLInputElement).valueAsNumber
    emit('update:modelValue', clamp(Number.isNaN(raw) ? 0 : raw))
}

function bump(direction: 1 | -1) {
    if (props.disabled) return
    emit('update:modelValue', clamp((props.modelValue ?? 0) + direction * props.step))
}

const canDecrement = computed(() => props.min === undefined || (props.modelValue ?? 0) > props.min)
const canIncrement = computed(() => props.max === undefined || (props.modelValue ?? 0) < props.max)

const fieldId = props.id ?? `p-number-${crypto.randomUUID().slice(0, 8)}`
</script>

<template>
    <div :class="['p-number', `p-number--${size}`, { 'p-number--error': error, 'p-number--disabled': disabled }]">
        <div v-if="label || suffix" class="p-number__label-row">
            <label v-if="label" :for="fieldId" class="p-number__label">
                {{ label }}
                <span v-if="required" aria-hidden="true" class="p-number__required">*</span>
            </label>
            <span v-if="suffix" class="p-number__suffix">{{ suffix }}</span>
        </div>
        <div class="p-number__control">
            <input
                :id="fieldId"
                type="number"
                class="p-number__native"
                :value="modelValue"
                :placeholder="placeholder"
                :disabled="disabled"
                :required="required"
                :min="min"
                :max="max"
                :step="step"
                :aria-invalid="error ? 'true' : 'false'"
                @input="onInput"
            />
            <div v-if="stepper" class="p-number__stepper">
                <button
                    type="button"
                    class="p-number__step"
                    tabindex="-1"
                    aria-label="Augmenter"
                    :disabled="disabled || !canIncrement"
                    @click="bump(1)"
                >
                    <Plus :size="12" :stroke-width="2" />
                </button>
                <button
                    type="button"
                    class="p-number__step"
                    tabindex="-1"
                    aria-label="Diminuer"
                    :disabled="disabled || !canDecrement"
                    @click="bump(-1)"
                >
                    <Minus :size="12" :stroke-width="2" />
                </button>
            </div>
        </div>
        <p v-if="error" class="p-number__error">{{ error }}</p>
        <p v-else-if="hint" class="p-number__hint">{{ hint }}</p>
    </div>
</template>

<style scoped>
.p-number {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.p-number__label-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--p-space-2);
}

.p-number__label {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    font-weight: var(--p-font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-4);
}

.p-number__required {
    color: var(--p-color-brick);
    margin-left: 4px;
}

.p-number__suffix {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    color: var(--p-color-ink-5);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
}

.p-number__control {
    position: relative;
    display: flex;
    align-items: stretch;
    background: var(--p-color-bg);
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    transition: border-color var(--p-motion-fast) var(--p-ease-out);
}

.p-number__control:focus-within {
    border-color: var(--p-color-ink-3);
}

.p-number--error .p-number__control {
    border-color: var(--p-color-brick);
}

.p-number--disabled .p-number__control {
    background: var(--p-color-surface-alt);
}

.p-number__native {
    width: 100%;
    min-width: 0;
    background: transparent;
    color: var(--p-color-ink);
    border: none;
    border-radius: var(--p-radius-sm);
    font-family: var(--p-font-sans);
    text-align: right;
    -moz-appearance: textfield;
    appearance: textfield;
}

.p-number__native:focus {
    outline: none;
}

.p-number__native:disabled {
    color: var(--p-color-ink-5);
    cursor: not-allowed;
}

.p-number__native::placeholder {
    color: var(--p-color-ink-5);
}

/* Masque les spinners natifs (WebKit). */
.p-number__native::-webkit-outer-spin-button,
.p-number__native::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.p-number__stepper {
    display: flex;
    flex-direction: column;
    border-left: 1px solid var(--p-color-line);
}

.p-number__step {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    width: 26px;
    padding: 0;
    background: transparent;
    border: none;
    color: var(--p-color-ink-4);
    cursor: pointer;
    transition:
        background var(--p-motion-fast) var(--p-ease-out),
        color var(--p-motion-fast) var(--p-ease-out);
}

.p-number__step:first-child {
    border-bottom: 1px solid var(--p-color-line);
}

.p-number__step:hover:not(:disabled) {
    background: var(--p-color-surface-alt);
    color: var(--p-color-ink);
}

.p-number__step:disabled {
    color: var(--p-color-ink-6);
    cursor: not-allowed;
}

/* Tailles : padding aligné sur PInput. */
.p-number--sm .p-number__native {
    padding: 6px 10px;
    font-size: var(--p-text-xs);
}
.p-number--md .p-number__native {
    padding: 8px 12px;
    font-size: var(--p-text-sm);
}
.p-number--lg .p-number__native {
    padding: 10px 14px;
    font-size: var(--p-text-base);
}

.p-number__error {
    margin: 0;
    color: var(--p-color-brick);
    font-size: var(--p-text-xs);
    line-height: var(--p-line-relaxed);
}

.p-number__hint {
    margin: 0;
    color: var(--p-color-ink-4);
    font-size: var(--p-text-xs);
    line-height: var(--p-line-relaxed);
}
</style>
